import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { Toast } from 'primereact/toast'

import {
  ApiError,
  ExcelPlanResponse,
  Item,
  OptimizePlanRequest,
  OptimizePlanResponse,
  Product_Output,
  ProductionPlanService,
  FilesService,
  Order_Output as Order,
  ProductionTask_Input,
  GenerateExcelRequest,
} from '@/api'
import { excelPlanResponseAtom, optimizePlanRequestAtom, optimizePlanResponseAtom, originalPlanResponseAtom } from '@/atoms/optimization'
import { normalizeOptimizePlanResponse } from '@/utils/normalizeOptimizeResponse'
import { ItemStats, ItemTreeTableProps } from '../components/ItemTreeTable'
import { extractToNumbersFromNumberWithBold, NumberWithBold } from '../types'
import { useMutationTaskWithProgress } from '@/api/hooks/useTaskWithProgress'

export type ExcelReloadStep = 'downloading' | 'reading' | 'optimizing'
export type ExcelReloadStage = ExcelReloadStep | 'idle' | 'success' | 'error'
export type ExcelReloadStatus = 'pending' | 'active' | 'completed' | 'error'
type ItemStatusFlag = 'empty' | 'little' | 'over'

export const excelReloadSteps: { id: ExcelReloadStep; label: string }[] = [
  { id: 'downloading', label: 'OneDriveから最新ファイルを取得' },
  { id: 'reading', label: 'Excelデータを再読み込み' },
  { id: 'optimizing', label: 'シミュレーションを再実行' },
]

const rebuildOptimizePlanRequestFromExcel = (
  prev: OptimizePlanRequest | null,
  planResponse: ExcelPlanResponse,
): OptimizePlanRequest | null => {
  if (!prev) {
    return null
  }

  const planDateIndex = new Map((planResponse.dates ?? []).map((date, idx) => [date, idx]))
  const excelProductions = planResponse.itemMaster.reduce<Record<string, Array<{ value: number; is_bold?: boolean }>>>((acc, item) => {
    acc[item.品目ID] = item.生産 ?? []
    return acc
  }, {})
  const excelShipments = planResponse.itemMaster.reduce<Record<string, Array<{ value: number }>>>((acc, item) => {
    acc[item.品目ID] = item.出荷 ?? []
    return acc
  }, {})
  const excelInventories = planResponse.itemMaster.reduce<Record<string, Array<{ value: number }>>>((acc, item) => {
    acc[item.品目ID] = item.在庫 ?? []
    return acc
  }, {})

  const existingItemIds = new Set<string>()
  prev.days.forEach((day) => {
    Object.keys(day.items ?? {}).forEach((itemId) => existingItemIds.add(itemId))
  })
  planResponse.itemMaster.forEach((item) => existingItemIds.add(item.品目ID))
  const targetItemIds = Array.from(existingItemIds)

  const updatedDays = prev.days.map((day) => {
    const sourceIdx = planDateIndex.get(day.date)
    const nextItems: typeof day.items = {}

    targetItemIds.forEach((itemId) => {
      const prevState = day.items?.[itemId]
      let production = prevState?.production
      let shipment = prevState?.shipment
      let inventory = prevState?.inventory

      if (sourceIdx !== undefined) {
        const prod = excelProductions[itemId]?.[sourceIdx]
        if (prod) {
          production = { value: prod.value, is_bold: !!prod.is_bold }
        }
        const ship = excelShipments[itemId]?.[sourceIdx]
        if (ship) {
          shipment = { value: ship.value }
        }
        const inv = excelInventories[itemId]?.[sourceIdx]
        if (inv) {
          inventory = { value: inv.value }
        }
      }

      if (!production) {
        production = { value: 0, is_bold: false }
      }

      nextItems[itemId] = {
        production,
        shipment,
        inventory,
      }
    })
    return { ...day, items: nextItems }
  })

  return { ...prev, days: updatedDays }
}

export type PlanActionBarConfig = {
  onSimulate: () => void
  onExcelReload: () => void
  onExcelOverwrite: () => void
  onAdjustSettings: () => void
  onCompare: () => void
  isSimulatePending: boolean
  isExcelPending: boolean
  isExcelReloading: boolean
  disableExcelReload: boolean
  disableExcelOverwrite: boolean
}

export const usePlanPage = () => {
  const toastRef = useRef<Toast>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const excelPlanResponse = useAtomValue(excelPlanResponseAtom)
  const [originalPlanResponse, setOriginalPlanResponse] = useAtom(originalPlanResponseAtom)
  const [optimizePlanResponse, setOptimizePlanResponse] = useAtom(optimizePlanResponseAtom)
  const optimizeRequest = useAtomValue(optimizePlanRequestAtom)
  const setExcelPlanResponse = useSetAtom(excelPlanResponseAtom)
  const setOptimizePlanRequest = useSetAtom(optimizePlanRequestAtom)

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [isCategory, setIsCategory] = useState(false)
  const [optimizerVisible, setOptimizerVisible] = useState(false)
  const [xmlModalVisible, setXmlModalVisible] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedMachine, setSelectedMachine] = useState<string | null>(null)
  const [excelReloadModalVisible, setExcelReloadModalVisible] = useState(false)
  const [excelReloadStage, setExcelReloadStage] = useState<ExcelReloadStage>('idle')
  const [excelReloadFailedStep, setExcelReloadFailedStep] = useState<ExcelReloadStep | null>(null)
  const [excelReloadError, setExcelReloadError] = useState<string | null>(null)
  const [userProductionChanges, setUserProductionChanges] = useState<Record<string, Record<number, NumberWithBold>>>({})
  const [excelWriteDialogVisible, setExcelWriteDialogVisible] = useState(false)
  const [excelWriteError, setExcelWriteError] = useState<string | null>(null)
  const [excelWriteLocked, setExcelWriteLocked] = useState(false)
  const [excelWriteSuccessful, setExcelWriteSuccessful] = useState(false)
  const [comparisonVisible, setComparisonVisible] = useState(false)
  const [excelPreviewVisible, setExcelPreviewVisible] = useState(false)
  const [excelPreviewError, setExcelPreviewError] = useState<string | null>(null)

  const isExcelReloading = excelReloadStage === 'downloading' || excelReloadStage === 'reading' || excelReloadStage === 'optimizing'

  const handleSelectionChange = useCallback((products: Product_Output[], category: boolean) => {
    setSelectedItemIds(products.map((product) => product.sku_id))
    setSelectedDate(null)
    setSelectedMachine(null)
    setIsCategory(category)
  }, [])

  // 比較パネルなどから品目を1つ選ぶ (品目の詳細を表示する)
  const selectSku = useCallback((skuId: string) => {
    setSelectedItemIds([skuId])
    setSelectedDate(null)
    setSelectedMachine(null)
    setIsCategory(false)
  }, [])

  const handleSelectedDateChange = useCallback((date: string | null) => {
    setSelectedDate(date)
    if (date) setSelectedMachine(null)
  }, [])

  const handleSelectedMachineChange = useCallback((machineId: string | null) => {
    setSelectedMachine(machineId)
    if (machineId) setSelectedDate(null)
  }, [])


  const resetExcelReloadState = useCallback(() => {
    setExcelReloadStage('idle')
    setExcelReloadFailedStep(null)
    setExcelReloadError(null)
  }, [])

  const closeExcelReloadModal = useCallback(() => {
    if (isExcelReloading) {
      return
    }
    setExcelReloadModalVisible(false)
    resetExcelReloadState()
  }, [isExcelReloading, resetExcelReloadState])

  const extractReloadErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { message?: string; body?: { detail?: string } }
      if (err.body?.detail) {
        return String(err.body.detail)
      }
      if (err.message) {
        return err.message
      }
    }
    return '不明なエラーが発生しました'
  }, [])

  const getReloadStepStatus = useCallback(
    (step: ExcelReloadStep): ExcelReloadStatus => {
      if (excelReloadStage === 'success') {
        return 'completed'
      }
      if (excelReloadStage === 'error') {
        if (excelReloadFailedStep === step) {
          return 'error'
        }
        if (!excelReloadFailedStep) {
          return 'pending'
        }
        const failedIndex = excelReloadSteps.findIndex((s) => s.id === excelReloadFailedStep)
        const stepIndex = excelReloadSteps.findIndex((s) => s.id === step)
        return stepIndex < failedIndex ? 'completed' : 'pending'
      }
      const currentIndex = excelReloadSteps.findIndex((s) => s.id === excelReloadStage)
      const stepIndex = excelReloadSteps.findIndex((s) => s.id === step)
      if (currentIndex === -1) {
        return 'pending'
      }
      if (currentIndex > stepIndex) {
        return 'completed'
      }
      if (currentIndex === stepIndex) {
        return 'active'
      }
      return 'pending'
    },
    [excelReloadStage, excelReloadFailedStep],
  )

  const excelReloadProgress = useMemo(
    () => excelReloadSteps.map((step) => ({ ...step, status: getReloadStepStatus(step.id) })),
    [getReloadStepStatus],
  )

  const currentResponse = optimizePlanResponse
  const orderProducts = useMemo(() => excelPlanResponse?.echoOrderProducts ?? [], [excelPlanResponse?.echoOrderProducts])
  const orders = useMemo(() => {
    const source = currentResponse?.orders ?? excelPlanResponse?.echoOrders ?? []
    const dayDates = currentResponse?.dates ?? []
    if (!dayDates.length) return source
    const lastDate = dayDates[dayDates.length - 1]
    const last = lastDate ? new Date(lastDate) : null
    if (!last || Number.isNaN(last.getTime())) return source
    const cutoff = new Date(last)
    cutoff.setDate(cutoff.getDate() + 20)
    return source.filter((o) => {
      const due = new Date(o.due_date)
      if (Number.isNaN(due.getTime())) return false
      return due <= cutoff
    })
  }, [currentResponse?.orders, currentResponse?.dates, excelPlanResponse?.echoOrders])

  const productionTasks = useMemo(() => {
    const source = currentResponse?.productionTasks ?? excelPlanResponse?.echoProductionTasks ?? []
    const dayDates = currentResponse?.dates ?? []
    if (!dayDates.length) return source
    const dateSet = new Set(dayDates)
    return source.filter((task) => dateSet.has(task.date))
  }, [currentResponse?.productionTasks, currentResponse?.dates, excelPlanResponse?.echoProductionTasks])

  const optimizationLogs = useMemo(() => currentResponse?.logs ?? [], [currentResponse?.logs])

  const inventoryAllocations = useMemo(
    () => currentResponse?.inventoryAllocations ?? [],
    [currentResponse?.inventoryAllocations],
  )

  const items = useMemo(() => {
    const sourceProducts: Product_Output[] = orderProducts.length ? orderProducts : excelPlanResponse?.echoProducts ?? []
    const flatten = (list: Product_Output[]): Product_Output[] => {
      const result: Product_Output[] = []
      const walk = (product: Product_Output) => {
        result.push(product)
        ;(product.components ?? []).forEach(walk)
      }
      list.forEach(walk)
      return result
    }

    const products = flatten(sourceProducts)
    if (!products.length) return excelPlanResponse?.itemMaster ?? []

    const horizon =
      currentResponse?.dates?.length ?? excelPlanResponse?.dates?.length ?? 0

    // 同一SKUが複数シート/機械に存在するケースを統合して重複IDを排除
    const merged = new Map<string, { product: Product_Output; machines: Set<string>; row: number }>()

    products.forEach((product, idx) => {
      const sku = product.sku_id
      const machine = (product.machine_sheet ?? '').trim() || '未指定'
      const row = (product as { row_index?: number })?.row_index ?? idx + 1
      if (!merged.has(sku)) {
        merged.set(sku, { product, machines: new Set([machine]), row })
      } else {
        const entry = merged.get(sku)!
        entry.machines.add(machine)
        entry.row = Math.min(entry.row, row)
      }
    })

    return Array.from(merged.values()).map(({ product, machines, row }): Item => {
      const machineLabel = Array.from(machines).join(', ')

      const emptyShipment = Array.from({ length: horizon }, () => ({ value: 0 }))
      const emptyProduction = Array.from({ length: horizon }, () => ({ value: 0, is_bold: false }))
      const emptyInventory = Array.from({ length: horizon }, () => ({ value: 0 }))

      // 在庫下限を計算: max_inventoryの20%または10（いずれか大きい方）
      const maxInventory = product.max_inventory ?? 0
      const minSafeStock = Math.max(Math.ceil(maxInventory * 0.2), 10)

      return {
        行: row,
        品目ID: product.sku_id,
        シート品目ID: product.machine_sheet_sku_id ?? '',
        品目名: product.name,
        最小発注単位: { value: product.min_lot_size ?? 0 },
        在庫下限: { value: minSafeStock },
        在庫上限: { value: maxInventory },
        工程時間: { value: product.quantity_per_cycle ?? 0 },
        当月生産計画: { value: 0, is_bold: false },
        次月生産計画: { value: 0, is_bold: false },
        生産上限: null,
        生産下限: null,
        分類: machineLabel,
        製造分類: product.category ?? '',
        廃番: '',
        データ不備: [],
        出荷: emptyShipment,
        生産: emptyProduction,
        在庫: emptyInventory,
      }
    })
  }, [currentResponse?.dates?.length, excelPlanResponse, orderProducts])

  const flatProducts = useMemo(() => {
    const sourceProducts: Product_Output[] = orderProducts.length ? orderProducts : excelPlanResponse?.echoProducts ?? []
    const result: Product_Output[] = []
    const walk = (product: Product_Output) => {
      result.push(product)
      ;(product.components ?? []).forEach(walk)
    }
    sourceProducts.forEach(walk)
    return result
  }, [orderProducts, excelPlanResponse])

  const buildProductInventory = useCallback(
    (dates: string[], sourceOrders: Order[], sourceTasks: ProductionTask_Input[]) => {
      if (!dates.length) return {}

      const dateSet = new Set(dates)

      const ordersByDateSku = sourceOrders.reduce<Record<string, number>>((acc, order) => {
        if (!dateSet.has(order.due_date)) return acc
        const key = `${order.due_date}__${order.sku_id}`
        acc[key] = (acc[key] ?? 0) + (order.quantity ?? 0)
        return acc
      }, {})

      const tasksByDateSku = sourceTasks.reduce<Record<string, number>>((acc, task) => {
        if (!dateSet.has(task.date)) return acc
        const key = `${task.date}__${task.sku_id}`
        acc[key] = (acc[key] ?? 0) + (task.quantity ?? 0)
        return acc
      }, {})

      // 親SKU→子コンポーネント、子SKU→親SKUリストのマッピングを構築
      // 注文は親SKUで発生するが、生産タスクは子コンポーネントで生成される(1:1で展開される)
      const parentsByChild = new Map<string, Product_Output[]>()
      flatProducts.forEach((parent) => {
        ;(parent.components ?? []).forEach((child) => {
          const list = parentsByChild.get(child.sku_id) ?? []
          list.push(parent)
          parentsByChild.set(child.sku_id, list)
        })
      })

      const calcInitialStock = (product: Product_Output): number => {
        const inv = product.inventories ?? {}
        if (inv['在庫合計'] && inv['在庫合計'].length) {
          return inv['在庫合計'].find((rec) => rec.date === dates[0])?.quantity
            ?? inv['在庫合計'][0]?.quantity
            ?? 0
        }
        return Object.values(inv).reduce((sum, records) => {
          if (!records || records.length === 0) return sum
          const match = records.find((rec) => rec.date === dates[0])?.quantity
          if (match !== undefined) return sum + match
          return sum + (records[0]?.quantity ?? 0)
        }, 0)
      }

      const result: Record<string, number[]> = {}

      flatProducts.forEach((product) => {
        let stock = calcInitialStock(product)
        const daily: number[] = []
        const ownDefectRate = product.defect_rate ?? 0
        const components = product.components ?? []
        const parents = parentsByChild.get(product.sku_id) ?? []

        dates.forEach((date) => {
          const ownTask = tasksByDateSku[`${date}__${product.sku_id}`] ?? 0
          let netProduction = ownTask

          // 親製品(componentsを持つ)の場合、各子コンポーネントの生産を加算
          for (const comp of components) {
            const compTask = tasksByDateSku[`${date}__${comp.sku_id}`] ?? 0
            const compDefectRate = comp.defect_rate ?? 0
            netProduction += compTask
          }

          let orderQty = ordersByDateSku[`${date}__${product.sku_id}`] ?? 0
          // 子コンポーネントの場合、親SKUの注文は1:1で展開されるため需要として加算
          for (const parent of parents) {
            orderQty += ordersByDateSku[`${date}__${parent.sku_id}`] ?? 0
          }

          stock = stock + netProduction - orderQty
          daily.push(stock)
        })

        result[product.sku_id] = daily
      })

      return result
    },
    [flatProducts],
  )

  const productInventory = useMemo(() => {
    return buildProductInventory(currentResponse?.dates ?? [], orders, productionTasks)
  }, [buildProductInventory, currentResponse?.dates, orders, productionTasks])

  const compareOrders = useMemo(() => originalPlanResponse?.orders ?? [], [originalPlanResponse?.orders])
  const compareProductionTasks = useMemo(
    () => originalPlanResponse?.productionTasks ?? [],
    [originalPlanResponse?.productionTasks],
  )
  const compareDates = useMemo(() => originalPlanResponse?.dates ?? [], [originalPlanResponse?.dates])
  const compareProductInventory = useMemo(() => {
    return buildProductInventory(compareDates, compareOrders, compareProductionTasks)
  }, [buildProductInventory, compareDates, compareOrders, compareProductionTasks])

  const productsById = useMemo(() => {
    const map: Record<string, Product_Output> = {}
    flatProducts.forEach((p) => {
      map[p.sku_id] = p
    })
    return map
  }, [flatProducts])

  const selectedItems = useMemo(() => {
    if (!selectedItemIds.length) {
      return []
    }
    const idSet = new Set(selectedItemIds)
    return items.filter((item) => idSet.has(item.品目ID))
  }, [items, selectedItemIds])
  const availableDates = useMemo(() => currentResponse?.dates ?? [], [currentResponse])

  const [itemsStatus, itemStats] = useMemo(() => {
    const status: ItemTreeTableProps['itemStatus'] = {}
    const stats: ItemTreeTableProps['itemStats'] = {}
    console.log({excelPlanResponse, currentResponse, orders, productionTasks, flatProducts})
    // 注文と生産タスクの集計（計画期間内の注文のみを対象とする）
    const planDates = currentResponse?.dates ?? excelPlanResponse?.dates ?? []
    const planDateSet = new Set(planDates)
    const ownOrdersBySku = (excelPlanResponse?.echoOrders ?? []).reduce<Record<string, number>>((acc, order) => {
      if (!planDateSet.has(order.due_date)) return acc
      acc[order.sku_id] = (acc[order.sku_id] ?? 0) + (order.quantity ?? 0)
      return acc
    }, {})

    const ownTasksBySku = (currentResponse?.productionTasks ?? excelPlanResponse?.echoProductionTasks ?? []).reduce<Record<string, number>>((acc, task) => {
      acc[task.sku_id] = (acc[task.sku_id] ?? 0) + (task.quantity ?? 0)
      return acc
    }, {})

    // 親SKU→子コンポーネントの注文/生産を伝播
    // - 注文は親SKUで発生し1:1で子に展開されるため、子の総注文数=親の総注文数
    // - 親製品の総成形数は、子コンポーネントの総成形数のminで決まる(組立可能数)
    const ordersBySku: Record<string, number> = { ...ownOrdersBySku }
    const tasksBySku: Record<string, number> = { ...ownTasksBySku }
    // 子コンポーネントのsku集合(同一skuが別シートで単独品目として読まれた際の自前注文と
    // 親からの伝播注文が二重計上されるのを防ぐため、子の注文は親からの伝播のみで再計算する)
    const childSkus = new Set<string>()
    flatProducts.forEach((parent) => {
      ;(parent.components ?? []).forEach((child) => childSkus.add(child.sku_id))
    })
    childSkus.forEach((sku) => {
      ordersBySku[sku] = 0
    })
    flatProducts.forEach((parent) => {
      const components = parent.components ?? []
      if (!components.length) return
      const parentOrders = ownOrdersBySku[parent.sku_id] ?? 0
      components.forEach((child) => {
        ordersBySku[child.sku_id] = (ordersBySku[child.sku_id] ?? 0) + parentOrders
      })
      const childTaskCounts = components.map((c) => ownTasksBySku[c.sku_id] ?? 0)
      tasksBySku[parent.sku_id] = Math.min(...childTaskCounts)
    })

    const inventoryByItem = buildProductInventory(
      currentResponse?.dates ?? excelPlanResponse?.dates ?? [],
      orders ?? [],
      productionTasks ?? [],
    )

    items.forEach((item) => {
      const itemId = item.品目ID
      const set = new Set<ItemStatusFlag>()

      const inventoryData = inventoryByItem[itemId] ?? []

      if (Array.isArray(inventoryData) && inventoryData.length > 0) {
        // 在庫マイナス判定
        if (inventoryData.some((value) => value < 0)) {
          set.add('empty')
        }

        // 在庫下限判定
        const minStock = item.在庫下限.value
        if (minStock > 0 && inventoryData.some((value) => value > 0 && value < minStock)) {
          set.add('little')
        }

        // 在庫上限判定
        const maxStock = item.在庫上限.value
        if (maxStock > 0 && inventoryData.some((value) => value > maxStock)) {
          set.add('over')
        }
      }

      status[itemId] = set

      stats[itemId] = {
        総注文数: ordersBySku[itemId] ?? 0,
        総成形数: tasksBySku[itemId] ?? 0,
      } satisfies ItemStats
    })

    return [status, stats]
  }, [items, currentResponse, excelPlanResponse, orders, productionTasks, buildProductInventory, flatProducts])

  const inventories = useMemo(() => {
    const result: Record<Item['品目ID'], number[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = currentResponse?.inventories?.[item.品目ID]?.map((i) => i.value) ?? []
      result[`${item.品目ID}_old`] = originalPlanResponse?.inventories?.[item.品目ID]?.map((i) => i.value) ?? []
    })
    return result
  }, [items, currentResponse, originalPlanResponse])

  const shipments = useMemo(() => {
    const result: Record<Item['品目ID'], number[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = currentResponse?.shipments?.[item.品目ID]?.map((i) => i.value) ?? []
      result[`${item.品目ID}_old`] = originalPlanResponse?.shipments?.[item.品目ID]?.map((i) => i.value) ?? []
    })
    return result
  }, [items, currentResponse, originalPlanResponse])

  const excelProductions = useMemo(() => {
    const result: Record<Item['品目ID'], NumberWithBold[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = item.生産?.map((p) => ({ value: p.value, is_bold: p.is_bold })) ?? []
    })
    return result
  }, [items])

  const optimizerProductions = useMemo(() => {
    const result: Record<Item['品目ID'], NumberWithBold[]> = {}
    items.forEach((item) => {
      result[item.品目ID] = currentResponse?.productions?.[item.品目ID]?.map((i) => ({ value: i.value, is_bold: i.is_bold })) ?? []
    })
    return result
  }, [items, currentResponse])

  const productions = useMemo(() => {
    const result: Record<Item['品目ID'], NumberWithBold[]> = {}
    items.forEach((item) => {
      const itemId = item.品目ID
      result[itemId] = currentResponse?.productions?.[itemId]?.map((i) => ({ value: i.value, is_bold: i.is_bold })) ?? []
      result[`${itemId}_old`] = originalPlanResponse?.productions?.[itemId]?.map((i) => ({ value: i.value, is_bold: i.is_bold })) ?? []
    })
    return result
  }, [items, currentResponse, originalPlanResponse])

  const numericOptimizerProductions = useMemo(
    () => extractToNumbersFromNumberWithBold(optimizerProductions),
    [optimizerProductions],
  )
  const numericProductions = useMemo(() => extractToNumbersFromNumberWithBold(productions), [productions])

  const selectedFileQuery = useQuery({
    queryKey: ['selectedFile'],
    queryFn: () => FilesService.selectedFiles(),
    enabled: true,
    refetchOnWindowFocus: false,
  })

  const optimizeQuery = useMutation({
    mutationFn: (req: OptimizePlanRequest) => ProductionPlanService.optimize(req),
    onSuccess: (data) => {
      setOriginalPlanResponse(normalizeOptimizePlanResponse(data))
    },
  })

  const generateExcelMutation = useMutationTaskWithProgress((req: GenerateExcelRequest) => ProductionPlanService.generateExcel(req))
  const previewExcelMutation = useMutationTaskWithProgress((req: GenerateExcelRequest) => ProductionPlanService.previewExcel(req))

  const extractExcelWriteErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { message?: string; body?: { detail?: string } }
      if (err.body?.detail) {
        return String(err.body.detail)
      }
      if (err.message) {
        return err.message
      }
    }
    return 'Excelファイルの更新に失敗しました'
  }, [])

  const isFileLockedError = useCallback((error: unknown): boolean => {
    if (error instanceof ApiError) {
      return error.status === 423
    }
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      return status === 423
    }
    return false
  }, [])

  const buildGenerateRequest = useCallback(
    (includeBold = false): GenerateExcelRequest | null => {
      if (!currentResponse?.productions || !currentResponse?.dates) {
        toastRef.current?.show({
          severity: 'warn',
          summary: '警告',
          detail: '生産計画データがありません。最適化を実行してください',
          life: 3000,
        })
        return null
      }

      return {
        productionTasks: productionTasks,
        dates: currentResponse.dates,
        lock: optimizeRequest?.lock ?? undefined,
        algorithm: currentResponse?.algorithm ?? undefined,
      }
    },
    [currentResponse, productions, productionTasks, optimizeRequest],
  )

  // 保存の前に、変わるセルを確認する (安全な書き戻し)
  const handleExcelOverwrite = useCallback(async () => {
    const excelRequest = buildGenerateRequest(true)
    if (!excelRequest) return
    setExcelPreviewVisible(true)
    setExcelPreviewError(null)
    try {
      await previewExcelMutation.mutateAsync([excelRequest])
    } catch (error) {
      setExcelPreviewError(extractExcelWriteErrorMessage(error))
    }
  }, [buildGenerateRequest, extractExcelWriteErrorMessage, previewExcelMutation])

  const executeExcelWrite = useCallback(async () => {
    const excelRequest = buildGenerateRequest(true)
    if (!excelRequest) return
    setExcelPreviewVisible(false)

    setExcelWriteDialogVisible(true)
    setExcelWriteError(null)
    setExcelWriteLocked(false)
    setExcelWriteSuccessful(false)

    try {
      await generateExcelMutation.mutateAsync([excelRequest])
      setExcelWriteSuccessful(true)
      toastRef.current?.show({
        severity: 'success',
        summary: 'Excel更新',
        detail: 'Excelファイルの書き込みが完了しました。',
        life: 3000,
      })
    } catch (error) {
      const message = extractExcelWriteErrorMessage(error)
      console.error('Excel出力エラー:', error)
      setExcelWriteError(message)
      setExcelWriteLocked(isFileLockedError(error))
      toastRef.current?.show({
        severity: 'error',
        summary: 'エラー',
        detail: message,
        life: 5000,
      })
    }
  }, [buildGenerateRequest, extractExcelWriteErrorMessage, generateExcelMutation, isFileLockedError])

  const handleExcelImport = useCallback(async () => {
    if (isExcelReloading) {
      return
    }

    let fileMeta = selectedFileQuery.data?.files?.[0]
    if (!fileMeta) {
      const refetched = await selectedFileQuery.refetch()
      fileMeta = refetched.data?.files?.[0]
    }

    if (!fileMeta) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: '再読み込み対象のファイルが見つかりません',
        life: 4000,
      })
      return
    }

    if (!fileMeta.onedrive_file_id) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: 'OneDriveとリンクされたファイルのみ再読み込みできます',
        life: 4000,
      })
      return
    }

    if (!optimizeRequest) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: '最適化設定が未設定のため再読み込みできません。生産計画作成フローを完了してください。',
        life: 5000,
      })
      return
    }

    resetExcelReloadState()
    setExcelReloadModalVisible(true)

    let currentStep: ExcelReloadStep = 'downloading'

    try {
      setExcelReloadStage('downloading')
      await FilesService.downloadOnedriveFile({ file_id: fileMeta.onedrive_file_id })
      currentStep = 'reading'
      setExcelReloadStage(currentStep)
      const planResponse = await ProductionPlanService.createProductionPlan(1)
      setExcelPlanResponse(planResponse)
      const updatedOptimizeRequest = rebuildOptimizePlanRequestFromExcel(optimizeRequest, planResponse)
      if (!updatedOptimizeRequest) {
        throw new Error('最適化設定の再構築に失敗しました')
      }
      setOptimizePlanRequest(updatedOptimizeRequest)

      currentStep = 'optimizing'
      setExcelReloadStage(currentStep)
      const simulationRequest: OptimizePlanRequest = {
        ...updatedOptimizeRequest,
        optimize: OptimizePlanRequest.optimize.SIMULATION_ONLY,
      }
      const optimizeResult = await ProductionPlanService.optimize(simulationRequest)
      const normalizedResult = normalizeOptimizePlanResponse(optimizeResult)
      setOriginalPlanResponse(normalizedResult)
      setOptimizePlanResponse(normalizedResult)

      setExcelReloadStage('success')
      setExcelReloadFailedStep(null)
      setExcelReloadError(null)
      queryClient.invalidateQueries({ queryKey: ['selectedFile'] })
      toastRef.current?.show({
        severity: 'success',
        summary: '再読み込み完了',
        detail: 'OneDriveの最新ファイルでシミュレーションを再実行しました',
        life: 4000,
      })
    } catch (error) {
      const message = extractReloadErrorMessage(error)
      setExcelReloadFailedStep(currentStep)
      setExcelReloadStage('error')
      setExcelReloadError(message)
      toastRef.current?.show({
        severity: 'error',
        summary: '再読み込み失敗',
        detail: message,
        life: 5000,
      })
    }
  }, [
    extractReloadErrorMessage,
    isExcelReloading,
    optimizeRequest,
    queryClient,
    resetExcelReloadState,
    selectedFileQuery,
    setExcelPlanResponse,
    setOptimizePlanResponse,
    setOptimizePlanRequest,
    setOriginalPlanResponse,
  ])

  const handleOptimizeDialogClose = useCallback(() => {
    if (optimizeQuery.isPending) {
      return
    }
    setOptimizerVisible(false)
  }, [optimizeQuery.isPending])

  const handleOptimize = useCallback(
    (response: OptimizePlanResponse, request: OptimizePlanRequest) => {
      setOptimizePlanRequest(request)
      setOptimizePlanResponse(normalizeOptimizePlanResponse(response))
      setOptimizerVisible(false)
    },
    [setOptimizePlanRequest, setOptimizePlanResponse],
  )

  const handleProductionChange = useCallback((itemId: string, dateIndex: number, value: number, isBold: boolean) => {
    setUserProductionChanges((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [dateIndex]: { value, is_bold: isBold },
      },
    }))
  }, [])

  const manualProductionPayload = useMemo(() => {
    const result: Record<string, Array<{ value: number; is_bold: boolean } | null>> = {}
    const dates = currentResponse?.dates ?? optimizeRequest?.days.map((day) => day.date) ?? []
    items.forEach((item) => {
      const itemId = item.品目ID
      const overrides: Array<{ value: number; is_bold: boolean } | null> = []

      dates.forEach((_, dateIndex) => {
        const userChange = userProductionChanges[itemId]?.[dateIndex]
        if (userChange?.is_bold) {
          overrides.push({ value: userChange.value, is_bold: true })
          return
        }

        const excelBold = item.生産?.[dateIndex]
        if (excelBold?.is_bold) {
          overrides.push({ value: excelBold.value, is_bold: true })
          return
        }

        const optimizerValue = currentResponse?.productions?.[itemId]?.[dateIndex]
        if (optimizerValue?.is_bold) {
          overrides.push({ value: optimizerValue.value, is_bold: true })
          return
        }

        overrides.push(null)
      })

      result[itemId] = overrides
    })

    return result
  }, [items, currentResponse, optimizeRequest, userProductionChanges])

  const previewOptimizeRequest = useMemo(() => {
    if (!optimizeRequest) return null
    const updatedDays = optimizeRequest.days.map((day, dayIndex) => {
      const nextItems: typeof day.items = {}
      Object.entries(day.items ?? {}).forEach(([itemId, state]) => {
        const overrides = manualProductionPayload[itemId]
        const overrideValue = overrides?.[dayIndex]
        if (overrideValue) {
          nextItems[itemId] = {
            ...state,
            production: { value: overrideValue.value, is_bold: true },
          }
        } else if (state.production && state.production.is_bold) {
          nextItems[itemId] = {
            ...state,
            production: { ...state.production, is_bold: false },
          }
        } else {
          nextItems[itemId] = state
        }
      })
      return { ...day, items: nextItems }
    })
    return { ...optimizeRequest, days: updatedDays }
  }, [manualProductionPayload, optimizeRequest])

  const executeOptimization = useCallback(() => {
    if (!previewOptimizeRequest) {
      toastRef.current?.show({
        severity: 'warn',
        summary: '警告',
        detail: '最適化リクエストが不正です',
        life: 3000,
      })
      return
    }
    optimizeQuery.mutate(previewOptimizeRequest)
    setOptimizerVisible(true)
  }, [optimizeQuery, previewOptimizeRequest])

  const hasSelectedFile = Boolean(selectedFileQuery.data?.files?.length)
  const isExcelWriting = generateExcelMutation.isPending || generateExcelMutation.isObserving

  const closeExcelWriteDialog = useCallback(() => {
    if (isExcelWriting) {
      return
    }
    setExcelWriteDialogVisible(false)
    setExcelWriteError(null)
    setExcelWriteLocked(false)
    setExcelWriteSuccessful(false)
  }, [isExcelWriting])

  const actionBarConfig: PlanActionBarConfig = {
    onSimulate: executeOptimization,
    onExcelReload: handleExcelImport,
    onExcelOverwrite: handleExcelOverwrite,
    onAdjustSettings: () => navigate({ to: '/create-plan' }),
    onCompare: () => setComparisonVisible(true),
    isSimulatePending: optimizeQuery.isPending,
    isExcelPending: isExcelWriting,
    isExcelReloading,
    disableExcelReload:
      !hasSelectedFile || isExcelWriting || isExcelReloading,
    disableExcelOverwrite:
      !hasSelectedFile || isExcelWriting || isExcelReloading,
  }

  return {
    toastRef,
    items,
    orderProducts,
    availableDates,
    excelPlanResponse,
    currentResponse,
    optimizationLogs,
    orders,
    productionTasks,
    inventoryAllocations,
    productInventory,
    compareOrders,
    compareProductionTasks,
    compareProductInventory,
    compareDates,
    itemsStatus,
    itemStats,
    productsById,
    inventories,
    shipments,
    productions,
    excelProductions,
    optimizerProductions,
    numericOptimizerProductions,
    numericProductions,
    selectedItems,
    isCategory,
    selectedDate,
    selectedMachine,
    handleSelectionChange,
    setSelectedDate: handleSelectedDateChange,
    setSelectedMachine: handleSelectedMachineChange,
    handleProductionChange,
    userProductionChanges,
    optimizerVisible,
    previewOptimizeRequest,
    optimizeQueryData: optimizeQuery.data,
    handleOptimize,
    handleOptimizeDialogClose,
    xmlModalVisible,
    setXmlModalVisible,
    setUserProductionChanges,
    actionBarConfig,
    comparisonDialog: {
      visible: comparisonVisible,
      close: () => setComparisonVisible(false),
      excelTasks: compareProductionTasks,
      currentTasks: productionTasks,
      currentKpis: currentResponse?.kpis ?? null,
      currentAlgorithm: currentResponse?.algorithm ?? null,
      optimizeRequest,
      selectSku,
    },
    excelPreviewDialog: {
      visible: excelPreviewVisible,
      isLoading: previewExcelMutation.isPending,
      lastLog: previewExcelMutation.lastLog,
      error: excelPreviewError,
      preview: previewExcelMutation.data ?? null,
      cancel: () => setExcelPreviewVisible(false),
      confirm: executeExcelWrite,
    },
    excelReloadDialog: {
      visible: excelReloadModalVisible,
      close: closeExcelReloadModal,
      progress: excelReloadProgress,
      stage: excelReloadStage,
      error: excelReloadError,
      isExcelReloading,
    },
    excelWriteDialog: {
      visible: excelWriteDialogVisible,
      close: closeExcelWriteDialog,
      isWriting: isExcelWriting,
      progress: generateExcelMutation.progress,
      lastLog: generateExcelMutation.lastLog,
      logs: generateExcelMutation.logs,
      error: excelWriteError,
      isLocked: excelWriteLocked,
      isSuccess: excelWriteSuccessful,
    },
  }
}
